/**
 * Admin-page visibility — read which pages/tabs are hidden because the
 * tenant's operation graph lacks the matching node, plus the platform
 * CRUD over the page→node rule set.
 *
 * Tenant read (``useAdminPageVisibility``) is called from
 * ``AdminLayout`` on every render to drive the nav filter and from
 * ``AdminPricing`` to filter the per-process tab list. Cache is
 * shared via the ``adminKeys.adminPageVisibility`` queryKey so the
 * two callers don't double-fetch.
 *
 * Platform CRUD (``useAdminPageVisibilityRules`` +
 * mutations) is consumed by the ``AdminPageVisibilityRules`` editor
 * page mounted on the platform-admin shell.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

import { adminKeys } from "../keys";

const API = "/api/v1";

export type AdminPageHideReason =
  | "markers"
  | "entitlements"
  | "both"
  /** Plán to dovoluje, ale předpoklad mimo plán ještě není hotový
   *  — dnes jediné: Pošta u tenanta bez ověřené domény (KAN-779). */
  | "not_ready";

export interface AdminPageVisibility {
  hidden_page_ids: string[];
  /**
   * Map of ``page_id -> reason`` ("markers" = operation-graph node
   * missing, "entitlements" = plan tier doesn't include it, "both" =
   * neither passes). Absent key = page not hidden. ``RequirePageVisible``
   * picks the 403 copy from this so a tier-gated page no longer shows
   * the "add a graph node" hint by mistake.
   */
  hidden_page_reasons: Record<string, AdminPageHideReason>;
  graph_markers: string[];
  /**
   * R3c day-1 režim — DRUHÝ zdroj skrývání s vlastním významem:
   * kosmetické per-user oříznutí navigace na kritickou cestu
   * Prodávat, nikdy security (na rozdíl od ``hidden_page_ids``
   * nevede na 403). Optional kvůli klientům s cachovanou starší
   * odpovědí — absence = režim vypnutý.
   */
  day_one?: {
    /**
     * EFEKTIVNÍ režim: preference uživatele (nebo odvozený default) a
     * zároveň nedokončený Rozjezd — dokončený ho ukončí (KAN-1891).
     */
    enabled: boolean;
    /**
     * KAN-1891: preference říká ON, ale dokončený Rozjezd režim ukončil.
     * Optional kvůli cachované starší odpovědi — absence = ne.
     */
    ended_by_onboarding?: boolean;
    /**
     * KAN-758 (day-1 2.0): moduly odemčené uživatelem přes modal
     * „Přesto se do toho pustit" — override nad stavem dat. Klíče =
     * nav group id; optional kvůli cachované starší odpovědi.
     */
    unlocked_modules?: string[];
  };
  /**
   * KAN-1890 — co skryté stránce APLIKACE v mapě chybí: podmínka
   * ``visible_when`` z manifestu (``marker``) a umístitelné operace z
   * palety mapy, které ji splní. Jen stránky ``app-<slug>-<page>`` s
   * nesplněnou podmínkou. Optional kvůli cachované starší odpovědi.
   */
  app_page_requirements?: Record<string, AppPageRequirement>;
}

export interface AppPageRequirement {
  marker: string | null;
  operations: {
    slug: string;
    label_cs: string;
    label_en: string;
    operation_kind: string;
  }[];
}

/**
 * Zapnout/vypnout day-1 režim pro PŘIHLÁŠENÉHO uživatele v aktivním
 * tenantovi (stránka Rozjezd, nabídka průvodce). Druhý konec režimu je
 * dokončený Rozjezd (KAN-1891) — ten rozhoduje BE a zapnutí po něm
 * vrátí ``enabled: false``.
 */
export function useSetDayOneMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      apiFetch<{ enabled: boolean }>(
        `${API}/admin/page-visibility/day-one`,
        { method: "PUT", body: { enabled } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.adminPageVisibility() });
    },
  });
}

/**
 * KAN-758 — „Přesto se do toho pustit": trvale odemknout jeden zamčený
 * modul pro přihlášeného uživatele. Jednosměrné (modal nenabízí zpětné
 * zamčení); druhá cesta ven ze zámku je splnit prerekvizity nebo
 * ukončit celý day-1 režim.
 */
export function useUnlockDayOneModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moduleKey: string) =>
      apiFetch<{ enabled: boolean; unlocked_modules: string[] }>(
        `${API}/admin/page-visibility/day-one/modules`,
        { method: "PUT", body: { module_key: moduleKey } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.adminPageVisibility() });
    },
  });
}

export interface AdminPageVisibilityRule {
  page_id: string;
  required_markers: string[];
  required_entitlements: string[];
  /**
   * KAN-1961 — klíče limitů tarifu (``Plan.max_*``), které musí být pro
   * tenanta nenulové (``null`` = bez limitu projde).
   */
  required_limits: string[];
  description: string | null;
  updated_at: string;
}

export interface AdminPageVisibilityRulesUpsertPayload {
  required_markers: string[];
  required_entitlements: string[];
  /** Vynechané = BE ponechá stávající limity řádku. */
  required_limits?: string[];
  description?: string | null;
}

/**
 * Effective hidden pages + reasons + graph markers for the active tenant.
 *
 * ``staleTime`` je krátký (10s), aby změna pravidla v Platform admin
 * UI (nebo v plan entitlementu) propadla do tenantových oken rychle.
 * ``refetchOnWindowFocus`` explicitně zapnuté (default je sice true,
 * ale uvádíme ho lokálně, aby případná globální změna QueryClient
 * defaults nepřišla o snappy refresh téhle queries). Operator změnu
 * v "Viditelnost admin stránek" tak vidí v tenantově session do 10s
 * od dalšího focusu / navigace; mutační invalidace (viz
 * ``useUpsertAdminPageVisibilityRule``) odbavuje vlastní session
 * okamžitě.
 */
export function useAdminPageVisibility(
  options?: Omit<
    UseQueryOptions<AdminPageVisibility>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<AdminPageVisibility>({
    queryKey: adminKeys.adminPageVisibility(),
    queryFn: () =>
      apiFetch<AdminPageVisibility>(`${API}/admin/page-visibility`),
    staleTime: 10 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Platform CRUD — list rules.
 *
 * Returns ``undefined`` for non-platform-admin callers (the endpoint
 * 403s); component guards on caller role before mounting the query.
 */
export function useAdminPageVisibilityRules() {
  return useQuery<AdminPageVisibilityRule[]>({
    queryKey: adminKeys.adminPageVisibilityRules(),
    queryFn: () =>
      apiFetch<AdminPageVisibilityRule[]>(
        `${API}/admin/platform/page-visibility-rules`,
      ),
    staleTime: 30 * 1000,
  });
}

/**
 * Podmínka stránky aplikace z manifestu (``visible_when``, KAN-1359).
 *
 * Nemá řádek v tabulce pravidel a needituje se — jediný zapisovač je
 * manifest. ``required_marker: null`` = podmínka nejde přečíst a
 * stránka se tenantům skrývá.
 */
export interface AdminAppPageRule {
  page_id: string;
  app_slug: string;
  version: string;
  required_marker: string | null;
}

export function useAdminPageVisibilityAppRules() {
  return useQuery<AdminAppPageRule[]>({
    queryKey: adminKeys.adminPageVisibilityAppRules(),
    queryFn: () =>
      apiFetch<AdminAppPageRule[]>(
        `${API}/admin/platform/page-visibility-rules/from-apps`,
      ),
    staleTime: 30 * 1000,
  });
}

export function useAdminPageVisibilityKnownMarkers() {
  return useQuery<{ markers: string[] }>({
    queryKey: adminKeys.adminPageVisibilityKnownMarkers(),
    queryFn: () =>
      apiFetch<{ markers: string[] }>(
        `${API}/admin/platform/page-visibility-rules/known-markers`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminPageVisibilityKnownEntitlements() {
  return useQuery<{ entitlements: string[] }>({
    queryKey: adminKeys.adminPageVisibilityKnownEntitlements(),
    queryFn: () =>
      apiFetch<{ entitlements: string[] }>(
        `${API}/admin/platform/page-visibility-rules/known-entitlements`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminPageVisibilityKnownLimits() {
  return useQuery<{ limits: string[] }>({
    queryKey: adminKeys.adminPageVisibilityKnownLimits(),
    queryFn: () =>
      apiFetch<{ limits: string[] }>(
        `${API}/admin/platform/page-visibility-rules/known-limits`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpsertAdminPageVisibilityRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      page_id,
      payload,
    }: {
      page_id: string;
      payload: AdminPageVisibilityRulesUpsertPayload;
    }) =>
      apiFetch<AdminPageVisibilityRule>(
        `${API}/admin/platform/page-visibility-rules/${encodeURIComponent(page_id)}`,
        {
          method: "PUT",
          body: payload,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: adminKeys.adminPageVisibilityRules(),
      });
      // Tenant view depends on these rules; invalidate too so the
      // admin shell reflects the new state on next render.
      qc.invalidateQueries({
        queryKey: adminKeys.adminPageVisibility(),
      });
    },
  });
}

export function useDeleteAdminPageVisibilityRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (page_id: string) =>
      apiFetch<void>(
        `${API}/admin/platform/page-visibility-rules/${encodeURIComponent(page_id)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: adminKeys.adminPageVisibilityRules(),
      });
      qc.invalidateQueries({
        queryKey: adminKeys.adminPageVisibility(),
      });
    },
  });
}
